import {useCallback, useMemo, useRef} from 'react'
import {GearIcon} from '@primer/octicons-react'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {pullRequestsOverviewRoute} from '../../routes/overview-route'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {useModifyMilestone} from '../../mutations/use-modify-milestone'
import {useMilestoneQuery} from './data/route-queries'
import {SidebarDisclosureHeader} from './SidebarDisclosureHeader'
import {MutationFailureBanner} from '../FailureBanner'

export function SidebarMilestonePicker() {
  const {data, isPending, isError} = useMilestoneQuery()
  const {owner, repo} = useRouteParams(pullRequestsOverviewRoute)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const modifyMilestoneMutation = useModifyMilestone()
  const {mutate} = modifyMilestoneMutation
  const currentMilestone = data?.milestone

  // Memoized so the picker receives a stable identity; a fresh object on every
  // render would clobber an in-progress selection.
  const activeMilestone: MilestonePickerMilestone | null = useMemo(
    () =>
      currentMilestone
        ? {
            // The picker is GraphQL-backed and keys its selection by node id.
            id: currentMilestone.nodeId,
            title: currentMilestone.title,
            closed: currentMilestone.closed,
            closedAt: currentMilestone.closedAt || null,
            dueOn: currentMilestone.dueOn || null,
            progressPercentage: currentMilestone.progressPercentage,
            url: currentMilestone.url,
          }
        : null,
    [currentMilestone],
  )

  const handleSelectionChange = useCallback(
    (selectedMilestones: MilestonePickerMilestone[]) => {
      // Selection is single-valued: picking replaces the milestone, deselecting clears it.
      const nextMilestone = selectedMilestones.at(-1) ?? null
      if (nextMilestone?.id === activeMilestone?.id) return

      if (nextMilestone) {
        mutate({action: 'add', milestone: nextMilestone})
      } else if (currentMilestone) {
        // Removal is keyed by database id rather than the picker's node id.
        mutate({action: 'remove', milestoneId: currentMilestone.id.toString()})
      }
    },
    [activeMilestone?.id, currentMilestone, mutate],
  )

  if (isPending) return <MilestoneHeader disabled aria-label="Loading milestone" />
  if (isError) return <MilestoneHeader disabled aria-label="Milestone couldn't be loaded" />
  if (owner === undefined || repo === undefined) return null

  return (
    <>
      <MilestonePicker
        repo={repo}
        owner={owner}
        activeMilestone={activeMilestone}
        canCreateMilestone={false}
        showMilestoneDescription
        onSelectionChange={handleSelectionChange}
        anchorElement={anchorProps => (
          <MilestoneHeader ref={anchorRef} aria-label="Manage milestone" {...anchorProps} />
        )}
      />
      <MutationFailureBanner
        mutation={modifyMilestoneMutation}
        title="Failed to update milestone"
        returnFocusRef={anchorRef}
        className="mt-2"
      />
    </>
  )
}

function MilestoneHeader(props: React.ComponentPropsWithRef<'button'>) {
  return <SidebarDisclosureHeader title="Milestone" trailingIcon={GearIcon} aria-label="Manage milestone" {...props} />
}
